import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect, useRef } from 'preact/hooks';
import { contactsApi } from '@/api/contacts';
import { useToast } from '@/hooks/useToast';
import { useLookups } from '@/hooks/useLookups';
import { X } from 'lucide-preact';
import { isValidEmail } from '@/utils/validation';
import { useAIForm } from '@/hooks';
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat';
import { TextBox, TextBoxField, Select } from '@/components';
/** Abort a LinkedIn lookup that runs longer than this, so the modal can never hang indefinitely. */
const LOOKUP_TIMEOUT_MS = 45000;
export function OnlineEventParticipantModal({ open, onClose, onAdd, workspaceId, webId, campaignId }) {
    const { showError } = useToast();
    const { getSpeakerTypes } = useLookups();
    const speakerTypeOptions = getSpeakerTypes() || [];
    const [step, setStep] = useState(1);
    const [enriching, setEnriching] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [positions, setPositions] = useState([]);
    const [contactId, setContactId] = useState('');
    const [name, setName] = useState('');
    const [profilePicUrl, setProfilePicUrl] = useState('');
    const [attemptedSave, setAttemptedSave] = useState(false);
    const [saving, setSaving] = useState(false);
    const lookupAbortRef = useRef(null);
    const aiFields = step === 2 ? [
        { aiName: 'speakerBio', formPath: 'bio', displayName: 'Speaker Bio', fieldType: 'textarea', maxLength: 1000 },
        { aiName: 'speakerTitle', formPath: 'title', displayName: 'Speaker Title', fieldType: 'text', maxLength: 200 },
    ] : [];
    const { form } = useAIForm({
        mode: 'onChange',
        defaultValues: { linkedInUrl: '', speakerType: '', companyId: '', companyName: '', title: '', email: '', bio: '' },
    }, {
        aiContext: {
            workspaceId: workspaceId || '',
            campaignId,
            webId,
            location: ChatLocations.WebSpeakerEditor,
            entityType: ChatEntityTypes.Web,
            entityId: webId,
            viewMode: ChatViewModes.Create,
        },
        fields: aiFields,
        loadedData: step === 2 ? { speakerName: name } : undefined,
        contextSuppressed: !open,
    });
    const { register, control, watch, reset, setValue } = form;
    const linkedInUrlValue = watch('linkedInUrl');
    const speakerTypeValue = watch('speakerType');
    const companyIdValue = watch('companyId');
    const companyNameValue = watch('companyName');
    const titleValue = watch('title');
    const emailValue = watch('email');
    const bioValue = watch('bio');
    // Convert speaker type options to SelectOption format
    const speakerTypeSelectOptions = speakerTypeOptions.map(o => ({
        value: o.Key,
        label: o.Description,
    }));
    // Reset when opened/closed
    useEffect(() => {
        if (!open)
            return;
        lookupAbortRef.current?.abort();
        lookupAbortRef.current = null;
        setStep(1);
        reset({ linkedInUrl: '', speakerType: '', companyId: '', companyName: '', title: '', email: '', bio: '' });
        setEnriching(false);
        setCompanies([]);
        setPositions([]);
        setContactId('');
        setName('');
        setProfilePicUrl('');
        setAttemptedSave(false);
        setSaving(false);
    }, [open, reset]);
    const linkedInValid = /^https:\/\/(?:[a-z]{2,3}\.)?linkedin\.com\/.+/i.test(linkedInUrlValue.trim()) && linkedInUrlValue.trim().length <= 2048;
    const linkedInRequiredError = !linkedInUrlValue.trim() && step === 1;
    const linkedInInvalidError = !!linkedInUrlValue.trim() && !/^https:\/\/(?:[a-z]{2,3}\.)?linkedin\.com\/.+/i.test(linkedInUrlValue.trim());
    const linkedInLengthExceeded = linkedInUrlValue.trim().length > 2048;
    const handleLookup = async () => {
        if (!linkedInValid)
            return false;
        const controller = new AbortController();
        lookupAbortRef.current = controller;
        let timedOut = false;
        const timeoutId = setTimeout(() => { timedOut = true; controller.abort(); }, LOOKUP_TIMEOUT_MS);
        setEnriching(true);
        try {
            const res = await contactsApi.lookupByLinkedInUrl(linkedInUrlValue.trim(), { includeCompanies: true, includeSchools: false }, controller.signal);
            if (res.data) {
                const d = res.data;
                const fullName = (d.FullName || `${d.FirstName || ''} ${d.LastName || ''}`.trim()).trim();
                setName(fullName || 'Unknown');
                setCompanies((d.Companies || []));
                setPositions((d.Positions || []));
                setProfilePicUrl(d.ProfilePicUrl || '');
                setContactId(d.Id || '');
                return true;
            }
            if (timedOut) {
                showError('Lookup timed out', 'The LinkedIn lookup took too long to respond. Please check the URL and try again.');
            }
            else if (!controller.signal.aborted) {
                showError('Lookup failed', res.error || 'No data returned');
            }
            // Otherwise the lookup was aborted because the modal was closed — stay silent.
            return false;
        }
        catch (e) {
            if (e.name !== 'AbortError')
                showError('Lookup error', e.message);
            return false;
        }
        finally {
            clearTimeout(timeoutId);
            if (lookupAbortRef.current === controller)
                lookupAbortRef.current = null;
            setEnriching(false);
        }
    };
    const handleClose = () => {
        lookupAbortRef.current?.abort();
        lookupAbortRef.current = null;
        setEnriching(false);
        onClose();
    };
    const handleSelectCompany = (cid) => {
        setValue('companyId', cid);
        if (!cid)
            return;
        const comp = companies.find(c => c.Id === cid);
        if (comp)
            setValue('companyName', comp.Name || comp.UniversalName || comp.NormalizedName || '');
        // derive latest position for company
        const rel = positions.filter(p => p.CompanyId === cid);
        if (rel.length) {
            const latest = rel.sort((a, b) => {
                const ad = a.StartDate ? Date.parse(a.StartDate) : 0;
                const bd = b.StartDate ? Date.parse(b.StartDate) : 0;
                return bd - ad;
            })[0];
            if (latest?.Title)
                setValue('title', latest.Title);
            // always refresh bio when company changes
            if (latest?.Description)
                setValue('bio', latest.Description.slice(0, 1000));
        }
    };
    const nameLengthExceeded = name.trim().length > 200;
    const companyNameLengthExceeded = companyNameValue.trim().length > 200;
    const titleLengthExceeded = titleValue.trim().length > 200;
    const emailLengthExceeded = emailValue.trim().length > 254;
    const bioLengthExceeded = bioValue.trim().length > 1000;
    const emailValid = isValidEmail(emailValue);
    const emailRequiredError = !emailValue.trim() && attemptedSave;
    const emailInvalidError = !!emailValue.trim() && !emailValid;
    const emailMaxLengthError = emailLengthExceeded;
    const titleRequiredError = !titleValue.trim() && attemptedSave;
    const titleMaxLengthError = titleLengthExceeded;
    const companyNameRequiredError = !companyNameValue.trim() && attemptedSave;
    const companyNameMaxLengthError = companyNameLengthExceeded;
    const nameRequiredError = !name.trim() && attemptedSave;
    const nameMaxLengthError = nameLengthExceeded;
    const speakerTypeInvalidError = !speakerTypeValue.trim() && attemptedSave;
    const canNextFromStep1 = linkedInValid && !enriching;
    const canSave = name.trim() && !nameLengthExceeded && titleValue.trim() && !titleLengthExceeded && companyNameValue.trim() && !companyNameLengthExceeded && linkedInValid && emailValue.trim() && emailValid && !emailLengthExceeded && speakerTypeValue.trim() && !bioLengthExceeded;
    const handleSave = async () => {
        setAttemptedSave(true);
        if (!canSave)
            return;
        setSaving(true);
        try {
            const formValues = form.getValues();
            await onAdd({
                ContactId: contactId || '',
                Name: name.trim(),
                Email: formValues.email.trim(),
                Title: formValues.title.trim(),
                CompanyName: formValues.companyName.trim(),
                CompanyId: formValues.companyId || undefined,
                Bio: formValues.bio.trim() || undefined,
                SpeakerType: formValues.speakerType.trim(),
            }, formValues.linkedInUrl.trim(), profilePicUrl || undefined);
            onClose();
        }
        catch (e) {
            showError('Add failed', e.message || 'Unable to add participant');
        }
        finally {
            setSaving(false);
        }
    };
    // Compute error strings for TextBox/Select components
    const linkedInError = linkedInRequiredError
        ? 'Speaker LinkedInProfileUrl is required.'
        : linkedInInvalidError && !linkedInLengthExceeded
            ? 'Speaker LinkedInProfileUrl must be a valid https://*.linkedin.com/ URL.'
            : linkedInLengthExceeded
                ? 'Speaker LinkedInProfileUrl exceeds maximum length of 2048 characters.'
                : undefined;
    const titleError = titleRequiredError
        ? 'Speaker Title is required.'
        : titleMaxLengthError
            ? 'Speaker Title exceeds maximum length of 200 characters.'
            : undefined;
    const emailError = emailRequiredError
        ? 'Speaker Email is required.'
        : emailInvalidError
            ? 'Speaker Email is invalid.'
            : emailMaxLengthError
                ? 'Speaker Email exceeds maximum length of 320 characters.'
                : undefined;
    const bioError = bioLengthExceeded
        ? 'Speaker Bio exceeds maximum length of 1000 characters.'
        : undefined;
    const speakerTypeError = speakerTypeInvalidError
        ? 'Invalid SpeakerType.'
        : undefined;
    const companyNameError = companyNameRequiredError
        ? 'Speaker Company Name is required.'
        : companyNameMaxLengthError
            ? 'Speaker Company Name exceeds maximum length of 200 characters.'
            : undefined;
    if (!open)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4", children: [_jsx("div", { className: "absolute inset-0 bg-slate-900/60 backdrop-blur-sm", onClick: handleClose }), _jsxs("div", { className: `relative w-full ${step === 2 ? 'max-w-3xl' : 'max-w-lg'} rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl p-5 flex flex-col gap-4`, children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("h3", { className: "text-lg font-semibold text-slate-900 dark:text-white", children: "Add Participant" }), _jsx("button", { type: "button", onClick: handleClose, className: "p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-300", "aria-label": "Close", children: _jsx(X, { className: "w-4 h-4" }) })] }), _jsxs("div", { className: "text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400", children: ["Step ", step, " of 2"] }), step === 1 && (_jsxs("div", { className: "space-y-4", children: [_jsx(TextBoxField, { control: control, name: "linkedInUrl", labelSize: "xs", label: "LinkedIn URL", type: "url", maxLength: 2048, disabled: enriching, placeholder: "https://www.linkedin.com/in/username", error: linkedInError }), _jsx("div", { className: "flex justify-end", children: _jsx("button", { type: "button", disabled: !canNextFromStep1, onClick: async () => { if (await handleLookup())
                                        setStep(2); }, className: "px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50", children: enriching ? 'Fetching\u2026' : 'Next' }) }), enriching && (_jsxs("div", { className: "flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400", children: [_jsx("div", { className: "w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" }), "Fetching speaker details from LinkedIn. This may take up to 30 seconds depending on profile data availability."] }))] })), step === 2 && (_jsxs("div", { className: "space-y-4", children: [_jsx(TextBox, { labelSize: "xs", label: "Name", name: "name", value: name, onChange: () => { }, disabled: true, error: nameRequiredError ? 'Speaker Name is required.' : nameMaxLengthError ? 'Speaker Name exceeds maximum length of 200 characters.' : undefined }), _jsx(Select, { labelSize: "xs", label: "Speaker Type", options: speakerTypeSelectOptions, placeholder: "Select type", error: speakerTypeError, value: speakerTypeValue, ...register('speakerType') }), companies.length > 0 ? (_jsx(Select, { labelSize: "xs", label: "Company", name: "companyId", options: companies.map(c => ({ value: c.Id, label: c.Name || c.UniversalName || c.NormalizedName || 'Company' })), placeholder: "Select company", value: companyIdValue, onChange: (e) => handleSelectCompany(e.currentTarget.value) })) : (_jsxs("div", { children: [_jsx("label", { className: "block text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1", children: "Company" }), _jsx("div", { className: "text-xs text-slate-500 dark:text-slate-400", children: "No companies returned. You can enter details manually." })] })), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsx(TextBoxField, { control: control, name: "companyName", labelSize: "xs", label: "Company Name", maxLength: 200, placeholder: "Company name", disabled: companies.length > 0 && !companyIdValue, error: companyNameError }), _jsx(TextBoxField, { control: control, name: "title", labelSize: "xs", label: "Title", maxLength: 200, placeholder: "Title", disabled: companies.length > 0 && !companyIdValue, error: titleError })] }), _jsx(TextBoxField, { control: control, name: "email", labelSize: "xs", label: "Email", type: "email", maxLength: 320, placeholder: "name@example.com", disabled: companies.length > 0 && !companyIdValue, error: emailError }), _jsx(TextBoxField, { control: control, name: "bio", labelSize: "xs", label: "Bio (optional)", multiline: true, rows: 5, maxLength: 1000, showCharCounter: true, placeholder: "Short bio", disabled: companies.length > 0 && !companyIdValue, error: bioError }), _jsxs("div", { className: "flex items-center justify-between pt-2", children: [_jsx("button", { type: "button", onClick: () => setStep(1), className: "px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700", children: "Back" }), _jsxs("button", { type: "button", disabled: !canSave || saving, onClick: handleSave, className: "px-4 py-2 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2", children: [saving && _jsx("div", { className: "w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" }), " ", saving ? 'Saving...' : 'Add Participant'] })] })] }))] })] }));
}
